import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: string; // e.g. max-w-xl | max-w-2xl
  maxHeight?: string; // e.g. h-[90vh] | sm:max-h-[85vh]
  preventClose?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  children,
  footer,
  maxWidth = "max-w-2xl",
  maxHeight = "max-h-[90vh] sm:max-h-[85vh]",
  preventClose = false,
}) => {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => setMounted(true), []);

  // Handle open / close with animation
  useEffect(() => {
    if (open) {
      setVisible(true);
      document.body.style.overflow = "hidden";
    } else {
      const timer = setTimeout(() => setVisible(false), 200);
      document.body.style.overflow = "";
      return () => clearTimeout(timer);
    }

    // Cleanup: Ensure overflow is restored when component unmounts
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // ESC close
  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open && !preventClose) onClose();
    };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [open, onClose, preventClose]);

  if (!mounted || !visible) return null;

  return createPortal(
    <div
      style={{ zIndex: 9999 }}
      className="fixed inset-0 z-9999 flex items-end sm:items-center justify-center px-3 sm:px-4 py-4 sm:py-8"
    >
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-200 ${
          open ? "opacity-100" : "opacity-0"
        }`}
        onClick={() => !preventClose && onClose()}
      />

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        className={`
                relative z-10 w-full ${maxWidth}
                ${maxHeight}
                bg-white dark:bg-[#111c44] rounded-2xl
                shadow-[0_30px_80px_-20px_rgba(0,0,0,0.45)] dark:shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)]
                flex flex-col
                transition-all duration-200 ease-out
                ${open ? "scale-100 opacity-100" : "scale-95 opacity-0"}
                border border-gray-100 dark:border-white/10
                `}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header (Fixed) */}
        {title && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-white/10 shrink-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
              {title}
            </h2>
            <button
              onClick={onClose}
              className="p-2 -mr-2 rounded-full text-gray-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-[#1b254b] transition"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
        )}

        {/* Body (Scrollable) */}
        <div
          ref={bodyRef}
          className="flex-1 overflow-y-auto px-6 py-4 bg-gray-50 dark:bg-[#0b1437]"
        >
          {children}
        </div>

        {/* Footer (Sticky) */}
        {footer && (
          <div
            className="
                        px-6 py-4 bg-white dark:bg-[#111c44] border-t border-gray-100 dark:border-white/10
                        flex justify-end gap-3 shrink-0
                        shadow-[0_-8px_16px_rgba(0,0,0,0.08)] dark:shadow-none
                    "
          >
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};
